/**
 * @platform/rlhf-elements — a reusable KitElement grading surface for the
 * platform's RLHF feedback loop (`@platform/rlhf-core`).
 *
 * Light-DOM custom elements — an annotate-on-selection text view, a
 * rubric-dimension grader with judge-calibration display, a pairwise chooser, a
 * session bar, and a hover-revealed thumbs signal — that emit review events into a host-provided {@link RlhfSessionStore}
 * (IoC via {@link RlhfSessionStoreToken}) and never persist. Registration is
 * EXPLICIT via the `defineRlhf*()` helpers (no import side effects); each installs
 * the default `--rlhf-*`-themable styles.
 */

import { installRlhfStyles } from "./src/styles"
import { RlhfAnnotatedText } from "./src/rlhf-annotated-text"
import { RlhfDimensionGrader } from "./src/rlhf-dimension-grader"
import { RlhfPairwise } from "./src/rlhf-pairwise"
import { RlhfSessionBar } from "./src/rlhf-session-bar"
import { RlhfThumbs } from "./src/rlhf-thumbs"

// Store seam (IoC).
export {
  RlhfSessionStoreToken,
  createRlhfSessionStore,
  type RlhfSessionStore,
  type RlhfSessionStoreImpl,
  type RlhfSessionStoreOptions,
} from "./src/store"

// Draft-autosave seam (IoC, optional) — pre-save seamless-input text across reloads.
export {
  RlhfDraftStoreToken,
  createLocalDraftStore,
  optionalDraftStore,
  draftKey,
  type RlhfDraftStore,
  type LocalDraftStore,
  type LocalDraftStoreOptions,
  type DraftStorage,
  type DraftScheduler,
} from "./src/drafts"

// The id/clock seam (test-injectable).
export { systemClock, type RlhfClock } from "./src/clock"

// Anchoring + segmentation (pure; exported for host reuse + testing).
export { buildAnchor, anchorFromRange, anchorFromSelection } from "./src/anchor"
export { segmentParagraph, type MarkSpec, type Segment } from "./src/segments"

// Styles + the reusable »seamless input« behavior seam.
export { RLHF_STYLES_CSS, installRlhfStyles } from "./src/styles"
export { seamAutoGrow } from "./src/seam"

// Elements + view models.
export { RlhfAnnotatedText, AnnotatedTextVm } from "./src/rlhf-annotated-text"
export { RlhfDimensionGrader, DimensionGraderVm } from "./src/rlhf-dimension-grader"
export { RlhfPairwise, PairwiseVm } from "./src/rlhf-pairwise"
export { RlhfSessionBar, SessionBarVm } from "./src/rlhf-session-bar"
export { RlhfThumbs, ThumbsVm, type StepIndex } from "./src/rlhf-thumbs"

/** Register `<rlhf-annotated-text>` (idempotent) + install default styles. */
export function defineRlhfAnnotatedText(doc: Document = document): void {
  installRlhfStyles(doc)
  if (!customElements.get("rlhf-annotated-text")) customElements.define("rlhf-annotated-text", RlhfAnnotatedText)
}

/** Register `<rlhf-dimension-grader>` (idempotent) + install default styles. */
export function defineRlhfDimensionGrader(doc: Document = document): void {
  installRlhfStyles(doc)
  if (!customElements.get("rlhf-dimension-grader")) customElements.define("rlhf-dimension-grader", RlhfDimensionGrader)
}

/** Register `<rlhf-pairwise>` (idempotent) + install default styles. */
export function defineRlhfPairwise(doc: Document = document): void {
  installRlhfStyles(doc)
  if (!customElements.get("rlhf-pairwise")) customElements.define("rlhf-pairwise", RlhfPairwise)
}

/** Register `<rlhf-session-bar>` (idempotent) + install default styles. */
export function defineRlhfSessionBar(doc: Document = document): void {
  installRlhfStyles(doc)
  if (!customElements.get("rlhf-session-bar")) customElements.define("rlhf-session-bar", RlhfSessionBar)
}

/** Register `<rlhf-thumbs>` (idempotent) + install default styles. */
export function defineRlhfThumbs(doc: Document = document): void {
  installRlhfStyles(doc)
  if (!customElements.get("rlhf-thumbs")) customElements.define("rlhf-thumbs", RlhfThumbs)
}

/** Register every RLHF element at once. */
export function defineRlhfElements(doc: Document = document): void {
  defineRlhfAnnotatedText(doc)
  defineRlhfDimensionGrader(doc)
  defineRlhfPairwise(doc)
  defineRlhfSessionBar(doc)
  defineRlhfThumbs(doc)
}

declare global {
  interface HTMLElementTagNameMap {
    "rlhf-annotated-text": RlhfAnnotatedText
    "rlhf-dimension-grader": RlhfDimensionGrader
    "rlhf-pairwise": RlhfPairwise
    "rlhf-session-bar": RlhfSessionBar
    "rlhf-thumbs": RlhfThumbs
  }
}
